import { useMemo } from 'react';
import { getPageConfig } from '../utils/configLoader';

export type PageType = 'home' | 'about' | 'services' | 'contact';

export interface PageConfig {
  title: string;
  description: string;
  keywords: string;
  structure: {
    hero?: {
      headlines: string[];
      subheadline: string;
    };
    services?: {
      title: string;
      items: Array<{
        title: string;
        description: string;
        icon: string;
      }>;
    };
    mission?: {
      title: string;
      content: string;
    };
    team?: {
      title: string;
      content: string;
    };
    email?: {
      title: string;
      items: string[];
    };
    linkedin?: {
      title: string;
      items: string[];
    };
    seo?: {
      title: string;
      items: string[];
    };
    form?: {
      title: string;
      fields: string[];
    };
    info?: {
      title: string;
      items: string[];
    };
  };
}

export interface SiteConfig {
  brandColor: string;
  brandGradient: string;
  pages: {
    [key in PageType]: PageConfig;
  };
  socialMedia: {
    linkedin: string;
    twitter: string;
    facebook: string;
  };
  contactEmail: string;
  domain: string;
  siteName: string;
}

export type ConfigWithPage = SiteConfig & {
  currentPage: PageConfig;
};

const defaultConfig: SiteConfig = {
  brandColor: '#0B60B1',
  brandGradient: 'linear-gradient(120deg, #0B60B1 60%, #1e90ff 100%)',
  pages: {
    home: {
      title: 'Home',
      description: 'ImpactQuill - Email Deliverability Solutions',
      keywords: 'email, deliverability, marketing',
      structure: {
        hero: {
          headlines: [
            'Transform Your Email Marketing',
            'Boost Your Deliverability',
            'Protect Your Domain Reputation'
          ],
          subheadline: 'Expert email deliverability solutions for businesses of all sizes'
        }
      }
    },
    about: {
      title: 'About',
      description: 'About ImpactQuill',
      keywords: 'about, company',
      structure: {}
    },
    services: {
      title: 'Services',
      description: 'Our Services',
      keywords: 'services',
      structure: {}
    },
    contact: {
      title: 'Contact',
      description: 'Contact Us',
      keywords: 'contact',
      structure: {}
    }
  },
  socialMedia: {
    linkedin: 'https://linkedin.com/company/impactquill',
    twitter: 'https://twitter.com/impactquill',
    facebook: 'https://facebook.com/impactquill'
  },
  contactEmail: 'contact@impactquill.com',
  domain: 'impactquill.com',
  siteName: 'ImpactQuill'
};

export const useSiteConfig = (page: PageType = 'home'): ConfigWithPage => {
  return useMemo(() => {
    const config = getPageConfig(page);
    return {
      ...defaultConfig,
      currentPage: {
        ...config,
        structure: {
          hero: {
            headlines: [
              'Transform Your Email Marketing',
              'Boost Your Deliverability',
              'Protect Your Domain Reputation'
            ],
            subheadline: 'Expert email deliverability solutions for businesses of all sizes'
          },
          ...config.structure
        }
      }
    } as ConfigWithPage;
  }, [page]);
}; 